/**
 * Heuro Cinema - Knowledge Graph Animation
 * Canvas-based animated graph with nodes, connections, and signals.
 */

interface Node {
  id: number;
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  radius: number;
  color: string;
  phase: number;
  speed: number;
  opacity: number;
  scale: number;
  birthTime: number;
  connectionCount: number;
}

interface Connection {
  from: number;
  to: number;
  progress: number;
  opacity: number;
  growing: boolean;
}

interface Signal {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  progress: number;
  speed: number;
  color: string;
  size: number;
}

interface GraphConfig {
  initialNodes: number;
  maxNodes: number;
  nodeSpawnInterval: number;
  connectionGrowthDelay: number;
  connectionGrowthInterval: number;
  maxConnectionsPerNode: number;
}

const defaultConfig: GraphConfig = {
  initialNodes: 10,
  maxNodes: 50,
  nodeSpawnInterval: 550,
  connectionGrowthDelay: 700,
  connectionGrowthInterval: 280,
  maxConnectionsPerNode: 3,
};

let animationId: number | null = null;
let isDarkMode = true;

export function setGraphDarkMode(dark: boolean): void {
  isDarkMode = dark;
}

export function restartGraph(): void {
  if (animationId) {
    cancelAnimationFrame(animationId);
  }
  initGraph();
}

export function initGraph(config: Partial<GraphConfig> = {}): void {
  const canvas = document.getElementById('graphCanvas') as HTMLCanvasElement | null;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const isMobile = window.innerWidth < 768;
  const mobileOverrides: Partial<GraphConfig> = isMobile
    ? { initialNodes: 6, maxNodes: 25, maxConnectionsPerNode: 2 }
    : {};
  const cfg = { ...defaultConfig, ...mobileOverrides, ...config };

  let nodes: Node[] = [];
  let connections: Connection[] = [];
  let signals: Signal[] = [];
  let startTime: number | null = null;

  const colorList = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899'];

  let width: number, height: number, centerX: number, centerY: number;
  let lastNodeSpawn = 0;
  let lastConnectionGrowth = 0;

  function getColors() {
    return {
      background: isDarkMode ? '#050508' : '#f8fafc',
      connectionBase: isDarkMode ? [100, 140, 200] : [100, 116, 139],
      glowAlpha: isDarkMode ? 0.28 : 0.18,
    };
  }

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    canvas!.width = window.innerWidth * dpr;
    canvas!.height = window.innerHeight * dpr;
    canvas!.style.width = window.innerWidth + 'px';
    canvas!.style.height = window.innerHeight + 'px';
    ctx!.scale(dpr, dpr);
    width = window.innerWidth;
    height = window.innerHeight;
    centerX = width / 2;
    centerY = height / 2;
  }

  function getRandomPosition(): { x: number; y: number } {
    const isMobile = width < 768;
    const marginLeft = isMobile ? 40 : 280;
    const marginRight = isMobile ? 40 : 280;
    const marginTop = isMobile ? 60 : 100;
    const marginBottom = isMobile ? 100 : 180;
    const avoidCenter = isMobile ? 50 : 90;

    let x: number, y: number, attempts = 0;
    do {
      x = marginLeft + Math.random() * (width - marginLeft - marginRight);
      y = marginTop + Math.random() * (height - marginTop - marginBottom);
      attempts++;
      if (attempts > 50) break;
    } while (Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2) < avoidCenter);
    return { x, y };
  }

  function createNode(timestamp: number): void {
    const pos = getRandomPosition();
    const color = colorList[nodes.length % colorList.length];
    nodes.push({
      id: nodes.length,
      x: pos.x,
      y: pos.y,
      baseX: pos.x,
      baseY: pos.y,
      radius: 2 + Math.random() * 3,
      color,
      phase: Math.random() * Math.PI * 2,
      speed: 0.12 + Math.random() * 0.2,
      opacity: 0,
      scale: 0,
      birthTime: timestamp,
      connectionCount: 0,
    });
  }

  function findNearestUnconnectedNode(node: Node): Node | null {
    let nearest: Node | null = null;
    let nearestDist = Infinity;

    nodes.forEach(other => {
      if (other.id === node.id || other.opacity < 0.5) return;
      const alreadyConnected = connections.some(c =>
        (c.from === node.id && c.to === other.id) ||
        (c.from === other.id && c.to === node.id)
      );
      if (alreadyConnected) return;

      const dist = Math.sqrt((node.x - other.x) ** 2 + (node.y - other.y) ** 2);
      if (dist < nearestDist && dist < 300 &&
          node.connectionCount < cfg.maxConnectionsPerNode &&
          other.connectionCount < cfg.maxConnectionsPerNode) {
        nearestDist = dist;
        nearest = other;
      }
    });
    return nearest;
  }

  function initNodes(timestamp: number): void {
    for (let i = 0; i < cfg.initialNodes; i++) {
      createNode(timestamp + i * 70);
    }
    lastNodeSpawn = timestamp;
    lastConnectionGrowth = timestamp + cfg.connectionGrowthDelay;
  }

  function hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function animate(timestamp: number): void {
    const colors = getColors();

    if (!startTime) {
      startTime = timestamp;
      initNodes(timestamp);
    }

    ctx!.fillStyle = colors.background;
    ctx!.fillRect(0, 0, width, height);

    // Spawn new nodes
    if (nodes.length < cfg.maxNodes && timestamp - lastNodeSpawn > cfg.nodeSpawnInterval) {
      createNode(timestamp);
      lastNodeSpawn = timestamp;
    }

    // Grow connections
    if (timestamp - lastConnectionGrowth > cfg.connectionGrowthInterval) {
      const candidates = nodes.filter(n => n.opacity > 0.7 && n.connectionCount < cfg.maxConnectionsPerNode);
      if (candidates.length > 0) {
        const node = candidates[Math.floor(Math.random() * candidates.length)];
        const target = findNearestUnconnectedNode(node);
        if (target) {
          connections.push({ from: node.id, to: target.id, progress: 0, opacity: 0, growing: true });
          node.connectionCount++;
          target.connectionCount++;
        }
      }
      lastConnectionGrowth = timestamp;
    }

    // Draw connections
    connections.forEach(conn => {
      const fromNode = nodes[conn.from];
      const toNode = nodes[conn.to];
      if (!fromNode || !toNode) return;

      if (conn.growing) {
        conn.progress = Math.min(conn.progress + 0.025, 1);
        if (conn.progress >= 1) conn.growing = false;
      }
      conn.opacity = Math.min(conn.opacity + 0.018, isDarkMode ? 0.22 : 0.3);

      if (fromNode.opacity > 0.1 && toNode.opacity > 0.1) {
        const midX = fromNode.x + (toNode.x - fromNode.x) * conn.progress;
        const midY = fromNode.y + (toNode.y - fromNode.y) * conn.progress;
        const [r, g, b] = colors.connectionBase;
        ctx!.beginPath();
        ctx!.moveTo(fromNode.x, fromNode.y);
        ctx!.lineTo(midX, midY);
        ctx!.strokeStyle = `rgba(${r}, ${g}, ${b}, ${conn.opacity * fromNode.opacity * toNode.opacity * (isDarkMode ? 1 : 0.6)})`;
        ctx!.lineWidth = 1;
        ctx!.stroke();
      }
    });

    // Spawn signals
    if (Math.random() < 0.03) {
      const completed = connections.filter(c => !c.growing && c.opacity > 0.12);
      if (completed.length > 0) {
        const conn = completed[Math.floor(Math.random() * completed.length)];
        const from = nodes[conn.from];
        const to = nodes[conn.to];
        if (from && to && from.opacity > 0.5 && to.opacity > 0.5) {
          const reverse = Math.random() > 0.5;
          signals.push({
            fromX: reverse ? to.x : from.x,
            fromY: reverse ? to.y : from.y,
            toX: reverse ? from.x : to.x,
            toY: reverse ? from.y : to.y,
            progress: 0,
            speed: 0.014 + Math.random() * 0.01,
            color: reverse ? to.color : from.color,
            size: 1.5 + Math.random() * 1.5,
          });
        }
      }
    }

    // Draw signals
    signals = signals.filter(signal => {
      signal.progress += signal.speed;
      if (signal.progress >= 1) return false;

      const x = signal.fromX + (signal.toX - signal.fromX) * signal.progress;
      const y = signal.fromY + (signal.toY - signal.fromY) * signal.progress;

      const gradient = ctx!.createRadialGradient(x, y, 0, x, y, signal.size * 3.5);
      gradient.addColorStop(0, hexToRgba(signal.color, isDarkMode ? 0.55 : 0.5));
      gradient.addColorStop(0.5, hexToRgba(signal.color, isDarkMode ? 0.15 : 0.12));
      gradient.addColorStop(1, 'transparent');
      ctx!.beginPath();
      ctx!.arc(x, y, signal.size * 3.5, 0, Math.PI * 2);
      ctx!.fillStyle = gradient;
      ctx!.fill();

      ctx!.beginPath();
      ctx!.arc(x, y, signal.size, 0, Math.PI * 2);
      ctx!.fillStyle = signal.color;
      ctx!.fill();

      return true;
    });

    // Draw nodes
    nodes.forEach(node => {
      const timeSinceBirth = timestamp - node.birthTime;
      if (timeSinceBirth > 0) {
        node.opacity = Math.min(node.opacity + 0.028, 1);
        node.scale = Math.min(node.scale + 0.045, 1);
      }

      const time = timestamp * 0.001;
      node.x = node.baseX + Math.sin(time * node.speed + node.phase) * 2.5;
      node.y = node.baseY + Math.cos(time * node.speed * 0.8 + node.phase) * 2.5;

      if (node.opacity > 0.05 && node.scale > 0.05) {
        const r = node.radius * node.scale;
        ctx!.globalAlpha = node.opacity;

        // Birth ripple
        if (timeSinceBirth < 350) {
          const p = timeSinceBirth / 350;
          ctx!.beginPath();
          ctx!.arc(node.x, node.y, r * (1 + p * 2), 0, Math.PI * 2);
          ctx!.strokeStyle = hexToRgba(node.color, (1 - p) * 0.25);
          ctx!.lineWidth = 1.2 * (1 - p);
          ctx!.stroke();
        }

        // Glow
        const glow = ctx!.createRadialGradient(node.x, node.y, 0, node.x, node.y, r * 3.5);
        glow.addColorStop(0, hexToRgba(node.color, colors.glowAlpha));
        glow.addColorStop(0.5, hexToRgba(node.color, colors.glowAlpha * 0.25));
        glow.addColorStop(1, 'transparent');
        ctx!.beginPath();
        ctx!.arc(node.x, node.y, r * 3.5, 0, Math.PI * 2);
        ctx!.fillStyle = glow;
        ctx!.fill();

        // Core
        ctx!.beginPath();
        ctx!.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx!.fillStyle = node.color;
        ctx!.fill();

        ctx!.globalAlpha = 1;
      }
    });

    // Central glow
    const pulse = Math.sin(timestamp * 0.0012) * 0.12 + 0.88;
    const centralSize = Math.min(width, height) * 0.1;
    const centralAlpha = isDarkMode ? 0.04 : 0.03;
    const central = ctx!.createRadialGradient(centerX, centerY, 0, centerX, centerY, centralSize);
    central.addColorStop(0, `rgba(59, 130, 246, ${centralAlpha * pulse})`);
    central.addColorStop(0.7, `rgba(59, 130, 246, ${centralAlpha * 0.25 * pulse})`);
    central.addColorStop(1, 'transparent');
    ctx!.beginPath();
    ctx!.arc(centerX, centerY, centralSize, 0, Math.PI * 2);
    ctx!.fillStyle = central;
    ctx!.fill();

    animationId = requestAnimationFrame(animate);
  }

  resize();
  animationId = requestAnimationFrame(animate);

  window.addEventListener('resize', () => {
    resize();
    startTime = null;
    nodes = [];
    connections = [];
    signals = [];
  });
}